import moment from "moment"
import { Guid } from "guid-typescript";
import BookingPassenger from "@/components/Booking/models/BookingPassenger";
import BookingState, { BookingStates } from '@/components/Booking/models/BookingStates';
import { BookingPhases } from '@/components/Booking/models/BookingPhases';
import DateHelper from '@/util/DateHelper';
import Helpers from '@/util/Helpers'
import IMovitPlace from './IMovitPlace';
import IBookingDetailsViewModel from './IBookingDetailsViewModel';
import IAddressViewModel, { AddressTypeEnum } from './IAddressViewModel';
import IBookingCreateRequestViewModel from './IBookingCreateRequestViewModel';
import IPassengerViewModel from './IPassengerViewModel';
import IProductViewModel from './IProductViewModel';
import IPriceViewModel from './IPriceViewModel';
import IFlightViewModel from './IFlightViewModel';
import IBookingUpdateRequestViewModel from './IBookingUpdateRequestViewModel';
import IBookingRequest from './IBookingRequest';
import User from '@/models/User';
import IPriceEstimateProductViewModel from './IPriceEstimateProductViewModel';
import IPriceEstimateRouteViewModel from './IPriceEstimateRouteViewModel';
import TimeOfDay from '@/components/Timetable/models/TimeOfDay';
import IPriceEstimate from './IPriceEstimate';
import Passenger from '@/components/Passenger/models/Passenger';
import TransportationPermission from '@/components/Passenger/models/TransportationPermission';

export class Booking {
    id: string;
    shortId: string;

    idNumber: number;
    deleted: boolean;
    startDate: Date;
    endDate: Date;
    requirements: string[] = [];

    pickupDetails: string;
    supplierPricingRefId: string;
    // treated as getter only on api
    // TODO: array of int, not number
    passengerIds: number[];
    passengers: IPassengerViewModel[];
    rateId: string;
    flights: IFlightViewModel[];

    unnamedPassengersCount: number;
    costCenterName: string;
    driverNotes: string;

    fromAddress: string = null;
    fromPlace: IAddressViewModel = null;

    toAddress: string = null;
    toPlace: IAddressViewModel = null;

     // FK: Destination
    // TODO: int, not number
    destinationId: number;
    
    note: string;
    // TODO: int, not number
    state: number;
    // TODO: int, not number
    durationTotalHours: number;
    // TODO: int, not number
    durationMinutes: number;   
    originalBookingId: string;

    createdAt?: Date;
    createdByEmail?: string;
    
    updatedAt?: Date;
    updatedByEmail?: string;

    orderedAt?: Date;
    orderedByEmail?: string;

    // Front-end only, flag if this is new
    creating: boolean;

    createWithAllDestinationPassengers: boolean = false;

    description: string;

    rejectedReason: string;

    isRecurrent: number;

    product: IProductViewModel;

    transportationPermission?: TransportationPermission;

    klass: object = Booking;

    constructor() {
        const guidString = Guid.create().toString().split('-').join('').toLowerCase();
        this.id = guidString;
        this.shortId = guidString.substring(0, 9);
    }

    get noteString(): string {
        return this.note ? this.note : '';
    }

    get idNumberWithHash(): string {
        return `#${this.idNumber.toString()}`;
    }

    get createdByNameFromEmail(){
        return Helpers.nameFromEmail(this.createdByEmail);
    }

    get hasEnoughTimeBeforeStart(): boolean{
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        momentStart.subtract(Booking.lockDaysBeforeStart, "days");
        return momentNow.isBefore(momentStart); 
    }

    get isLocked(): boolean {
        if (Booking.nonLockingStates.includes(this.stateObj)){
            return false;
        }
        if (Booking.lockingStates.includes(this.stateObj)){
            return true;
        }
        return this.hasExpired;
    }

    get isAccepted(): boolean {
        return this.stateObj === BookingStates.Ok;
    }

    get isOrdered(): boolean {
        return this.stateObj !== BookingStates.Undefined;
    }

    get wasOrdered(): boolean {
        return ![
            BookingStates.Open, 
            BookingStates.Ok,
            BookingStates.Cancelled,
            BookingStates.Rejected,
            BookingStates.Finished,
        ].includes(this.stateObj);
    }

    get isOrderable(): boolean {
        if (!Booking.nonLockingStates.includes(this.stateObj)){
            return false;
        }
        return this.hasEnoughTimeBeforeStart;
    }
    get isOrderIsCancelable(): boolean {
        return this.stateObj === BookingStates.Open;
    }
    get hasExpired(): boolean {
        return !this.hasEnoughTimeBeforeStart;
    }
    get hasStartDatePassed(): boolean {
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        return momentNow.isSameOrAfter(momentStart); 
    }
    get daysUntilStart(): number {
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        return momentStart.diff(momentNow, 'days');
    }   
    get stateObj(){
        return BookingStates.States.find(s => s.id === this.state);
    }
    get stateName(){
        return this.stateObj.name;
    }
    get stateNameLowerCase(){
        return this.stateObj.nameLowerCase;
    }
    get currentStateColor(){
        return this.stateObj.color;
    }
    get currentStateAlertClass(){
        return this.stateObj.alertClass;
    }

    get bookingDate() {
        return this.startDate ? this.startDate : this.endDate;
    }

    get phase(){
        if (!this.hasStartDatePassed){
            if (this.hasEnoughTimeBeforeStart){
                return BookingPhases.NotStarted;
            } else {
                return BookingPhases.AlmostStarted;
            }
        } else {
            if (this.stateObj === BookingStates.Finished){
                return BookingPhases.Ended;
            }
            return BookingPhases.Ongoing;
        }
    }
    get i18nStateAndPhaseKey(){
        return "booking.state." + this.stateObj.name.toLowerCase() + ".phase." + this.phase.name.toLowerCase();
    }
    get startDateString() {
        const YYYY = this.bookingDate.getFullYear();
        const MM = (this.bookingDate.getMonth() + 1).toString().padStart(2, "0");
        const DD = (this.bookingDate.getDate()).toString().padStart(2, '0');
        return YYYY + "-" + MM + "-" + DD;
    }

    set startDateString(val) {
        const valDate = new Date(val + ' ');
        valDate.setHours(this.startDate.getHours(), this.startDate.getMinutes(), 0, 0);
        this.startDate = valDate;
    }

    get startTimeString() {
        const HH = this.bookingDate.getHours().toString().padStart(2, '0');
        const mm = this.bookingDate.getMinutes().toString().padStart(2, '0');
        return HH + ":" + mm;
    }

    set startTimeString(val) {
        const valTime = new TimeOfDay(0, 0);
        valTime.value = val;
        this.startDate.setHours(valTime.hours);
        this.startDate.setMinutes(valTime.minutes);
        this.startDate.setSeconds(0);
        this.startDate.setMilliseconds(0);
    }

    get startDateTimeString() {
        const YYYY = this.bookingDate.getFullYear();
        const MM = (this.bookingDate.getMonth() + 1).toString().padStart(2, "0");
        const DD = (this.bookingDate.getDate()).toString().padStart(2, '0');
        return YYYY + "-" + MM + "-" + DD;
    }

    get isRejected(): boolean {
        return this.state === BookingStates.Rejected.id;
    }

    get passengerCount(): number {
        return this.unnamedPassengersCount + this.namedPassengersCount;
    }

    get namedPassengersCount(): number {
        return this.passengers ? this.passengers.length : 0;
    }

    get primaryPassenger(): IPassengerViewModel | undefined {
        return this.passengers && this.passengers.length > 0 
            ? this.passengers[0]
            : undefined;
    }

    get primaryPassengerName(): string {
        return this.primaryPassenger.name !== this.createdByEmail ? this.primaryPassenger.name : "";
    }
   
    public static get mockPriceEstimate() {
        return { 
            route: {
                distance: 96678,
                duration: 4616,
                // tslint:disable-next-line
                geometry: "}tzrJmtluCACcA_CMWlB{FHWzAnDHR`D`HFV@DP`@HVLRJQRc@`AaCh@oAz@bBFJ\\f@NPTVNJRHb@Hb@Dn@?b@AX?zAQLAJC^Gl@S|Am@TILEl@SPKH~@JfAf@rDFXPr@VhBp@rENdATjBLfBHnA@`B@xA?vAE|DArEAfCArACrAEtAIvBMnBYfEYlDMbBStCSrDSdEOfEM`FAJMfFGzEGbKAfAAbBAn@CxDEbHC~E?LI~EKtFMnJEzBExAS|EStEa@rHi@tHgCh\\oAfP]|F[dGUjGMdFE|F?zEBzDFhDJdEPvDL`CRrCZtD^pD`@tDh@fEj@fEn@fE|@xFhAlGt@vDp@`DdA|En@dCj@bCdAzDzAhF|AjFtCfIpAlDdAxCx@rCh@tBPr@l@tCd@rCt@~Eh@pE`@rEX`FNhEJrEDxE?RBxHDbFHzFf@dNBn@LnCp@nIz@zIFl@`BbMrArJZdC\\zD\\dHBdABl@xAy@d@a@Zk@Ne@Ha@NsAh@gGf@oEd@wCz@{DnAiEtBgGvGgKpEaGjA}ArIqJpJiJ~KyI~NwJtN_IbF}B`CmAjHwDlEgChFyC~CqBdD{BnE_DxDsCvIcHdKqJf@g@rLsKhQ}NjLqItHwEfa@mUvAy@pGyD|ByAxFaE`H_GxCwCdDwD`EaF~GwJbE}GzFyKdBsDzBeFzAsDt@mBb@eA`BuEhBmF|BuH|FkR|FeUxC_MjDiOnSe}@jOgp@jDaOtAeGfIs]`A}DvEyPzEmOv@_ClH}RpIoRvJkRhI_OtEqHdDoFfP}T`EmFbFoGxIcKtGiHhGmG`E{D`FwEzCoCbFmErJoIdKkG|CcBdEqBjH}CrPcIvQkL|KwIdLqKnLgMjNaQr~@ulA`O_RnKgLpJ_J|KaJtJaHtTmN|G{ExIoHd`@s^xIyG|HiF\\StBgA|BkAzCiAfFoBvEmApBc@jC]lC[hF[X?bA?xAAlB@`CFfBJxCXjC\\p@JjCb@`Dz@nErA`E~ARHpH`ClFnArDt@nDl@rF`@xBHnGFjCCtJY~E[tE[fCUbDa@xDi@xDo@fEs@`Ck@dCs@xDkAjCaAvD{A`Bu@tAk@nCuAhDoBpDwB|CkBfQeLfBcAnCqA~Ao@hBu@xBs@j@OlBi@nDw@hAWnAS\\IjGsAjFiAtEmA^KfEmAdDgArBy@hHgCxMaGzJeFjKkG`IiFzDsC~AmA`BmAdE_DhIoHnHcH`H}H|FoH`B{BpBsCbGiJ~B}DvB{D|CyFvBgExDkInDcIxGcPfCiGrRae@xNu]fHuPzEkKfDcHvAgCpDsGl@cA|@yArBcDfGyIfCcDpGaIbF{FzGgHzIaJzJ_JzNuMxAuAjBaBhGeF~DcDlDcCjG}DtEcCnFaCvEmBjEyAzEkA|FcAbFo@tM_AvMc@~USbQJ~CJtJ`@`R`ApFh@lHdApF`AjHjBrGlBdCx@bFnBhBr@xJzE~GrD~NnIjAr@hBdA~KdFjLjEnKpC`OpCjJ~@lCLlI^dHBvCGdGY`Fw@`D_AfGqBdIqDrLkIrL{LhEmFnVk]dGyHrGsHjBsBbBgBtGqGhAcAxEeEvIcH~AkAnAgAxB_BpDqCzDgDpGoGnEmFhDqEh@u@hDaF|AaCjBaDjBwCjCyDfFoHlG}HrDwD|HmGbIsFrGcDvKwDfPqD|JiBjsBea@vE_Adr@cNxKyBnAWzKyBrh@iKxCk@~Cc@~@OhBUxJ_AhEUxCQbACbEItQBlHXhEZxEZbGp@bBXpUtD`Ef@|NtA~MHbLWtBOhMcAhJg@|IO`MBh@DjEPxDVvC\\`DZ|BZnDn@t@LbCd@rB`@jHhBfBd@nI~BnKrD|LtEbFzBrAl@jErBxKvFb@X~IhF|GdE`HxErMvJvEnDhWjUjHlGhDjChLbIdIdEvBbA~J|DpCz@bD|@fCn@`F`AtAVxCb@vCTxKl@zFD`JA~CBnEJdEVjFh@~Cf@~Dx@tEjAhEvAdBj@|Ar@fF|BtBfA|EvC`FjDdD`CzElDhFnD~FdDvDnBpIxCbD`ApGpA`Ep@`BPvF`@|CJnD@~EG^CnHg@rM{@`IWzIB|DPdCJjGh@hIjAjI~AbIvBzHnCnNxF~FzBrFlBvGfBbFdA~F`A~En@fCTrDTrCD|CHlB@pEGvKe@lEa@zEi@|PkCpK_CbE{AtAe@fCaAnFyBjHkEzEaD~B_BvV_QpQyJvQyI|RsJhGoE|BuB~C}C|D}EhAyAlMoQdRwW~A{BtOqTtFyHpDuExAcBdFiFx@s@VStC}B~FqDbDaBbFqBv@Ur@UjCk@dB]`KwAnAWxEuAd@OxC{A~AaAzD{CfCiCdDcE`CkDvGsKNWvB_D|BuCx@{@z@aAdAcAzAoAx@q@hBuAlAcAfB_Bv@w@xBgCbD}D~@iAzBuCdBgBjBwAjFqDhBgAxLcIbBgAfEgCvEkCtCaBnDqBjFyCnC}AjH}DvAy@~HmErCoBjBcBtB_C^g@rAiBtBkCrA{AhAeArAy@r@_@t@U`AUfAMpAGnBAfUY`D[\\E`Dg@|FaBZIbBg@fAa@dDsAj@SvBeAxC{A~CkBpB{A~BkBpAkAfBaB~AyApJwInMqLlHyGlKqJ~@}@bA}@dA_ApBiBjB}AzCqCzBsB|@w@`TuRrW_VjAs@`As@bE}CnCaCpBuB|B_Cr@y@lBcCjF{ErHaHzAsAtEcEfA{@hAs@jBgAjCkAhGwBfJwC~Ae@rC}@t@Oz@K~@KfAGlA?vAFl@Dn@HxAZz@Tr@T`A`@nAr@pBlAnA|@ZTl@j@nAhA~@|@lCrCzBjCz@fAvBnCjAhBvCvEdAdBhAhBrArBb@h@v@`AtArAdAt@|@d@fA\\j@J\\H`CNjAFN@\\@N@`@BdEJdA@V@vBDJ?P?lACdBENAjBErBEVAZ?TAPAtDKD?jCI\\?NCZIZMTKTO^Yl@e@tBeBPO~AqA\\YPKVKPGHAv@GLCPEJCzAKp@ET@L@TAvAGVANCD?DAbAKL?H@LBn@ZRFN?JEFEHMPs@FWhAsEDOz@eDDMH[H]~@wDXiAh@uBFUFYj@{B|AsGFWjBwHF[Le@Lg@p@mCNc@HYBKr@sCRu@J_@Li@\\mAn@kBx@aCL_@HSVy@t@yBf@sAL_@BS@S@S?Y?q@IiE?]AQAm@HMFIzBsCDELMV[TWBCDGDGdCeDDEHMx@gABEJODGj@aAtA{Bv@qA@AJQlBuCDEJSr@iArCwEDIDGDIFKPWHOHMNONUBEHQFIJ[F[@Y@[Am@Aa@Cc@Co@AU?EQuEAMCk@AUMoCAc@?K@W@OBSDSl@oCJUDQBQBUBa@@M@OBQCy@QGKEkAg@KGIGGIGMEYCUa@aFIcAEg@MiAe@kGa@sFeAeNE[IqAAMAI@a@UoCOsBOkA_@wEEq@Cs@AW"
            },
            products: [
                {
                    kilometers: 96.6784,
                    startAreaFee: 2.0000,
                    flatRate: 0.0,
                    total: 197.3600,
                    currency: "€",
                    priceCategory: {
                        id: 1,
                        name: "Perushinta",
                        description: "",
                        bookingsAdvanceMinimumMins: 180,
                        bookingsAdvanceBetweenFrom: 360,
                        bookingsAdvanceBetweenTo: 1380,
                        maxPassengers: 4,
                        maxLuggage: 2,
                        startFee: 2.0000,
                        kmFee: 2.0000,
                        minimumFee: 5.0000
                    }                       
                }
            ],
            startTime: new Date("2019-08-05T11:40:00Z"),
            startPlace: {
                id: 0,
                alias: "Tapailanpiha, 14200 Janakkala, Suomi",
                streetAddress: "Tapailanpiha, 14200 Janakkala, Suomi",
                postalcode: null,
                city: null,
                latitude: 60.926403399999991,
                longitude: 24.645961599999964,
                note: null,
                placeType: 0,
                color: null
            },
            endPlace: {
                id: 0,
                alias: "Helsinginkatu, Helsinki, Suomi",
                streetAddress: "Helsinginkatu, Helsinki, Suomi",
                postalcode: null, 
                city: null,
                latitude: 60.1863242,
                longitude: 24.943838400000004,
                note: null,
                placeType: 0,
                color: null
            },
            timitDestinationId: null
        }

    }

    public static fromApiBookingDetailsViewModel(vm: IBookingDetailsViewModel): Booking {
        const booking = new Booking();
        const bookingState = BookingStates.States.find(s => s.name.toLowerCase() === vm.state.toLowerCase())
        booking.id = vm.request.bookingRefId;
        booking.shortId = vm.request.shortId;
        booking.destinationId = vm.request.timitDestinationId;
        booking.createdAt = vm.createdAt;
        booking.state = bookingState ? bookingState.id : BookingStates.Undefined.id;
        if (vm.request.pickupDateTime) {
            booking.startDate = new Date(vm.request.pickupDateTime);   
            booking.endDate = null;     
        }
        if (vm.request.dropoffDateTime) {
            booking.endDate = new Date(vm.request.dropoffDateTime);        
            booking.startDate = null;
        }
        booking.fromPlace = vm.request.addresses
            .find((a: IAddressViewModel) => a.type === AddressTypeEnum.Origin)

        booking.toPlace = vm.request.addresses
            .find((a: IAddressViewModel) => a.type === AddressTypeEnum.Destination)
           
        booking.fromAddress = booking.fromPlace.address;
        booking.toAddress = booking.toPlace.address;
        booking.requirements = vm.request.requirements;
        booking.unnamedPassengersCount = Math.max(vm.request.product.pax, vm.request.passengers.length);
        booking.product = vm.request.product;
        booking.passengers = vm.request.passengers ? vm.request.passengers : [];
        booking.createdByEmail = vm.request.passengers[0].email;
        booking.driverNotes = vm.request.notes;
        booking.pickupDetails = vm.request.pickupDetails;
        booking.supplierPricingRefId = vm.request.supplierPricingRefId;
        booking.rateId = vm.request.rateId;
        booking.flights = vm.request.flights;
        return booking;
    }

    public static toApiBookingRequestViewModel(
        booking: Booking, 
        priceEstimateRoute: IPriceEstimateRouteViewModel, 
        selectedPriceEstimateProduct: IPriceEstimateProductViewModel,
        passengerName: Passenger | string,
        isDropoffTime: boolean
    ): any {

        let passenger: Passenger;
        if (typeof(passengerName) === 'string') {
            passenger = new Passenger({ name: passengerName });
        } else {
            passenger = passengerName;
        }

        const bookingRequest: IBookingRequest = {
            movitId: passenger.movitId,
            name: passenger.name,
            email: booking.createdByEmail,
            phone: "", // TODO: Populate phone number
            flight: "", // TODO: Populate flight info
            requests: booking.note, // TODO: Populate requests
            timitDestinationId: booking.destinationId,
            product: selectedPriceEstimateProduct,
            startTime: booking.startDate,
            endTime: booking.endDate,
            startPlace: this.addressToMovitAddress(booking.fromPlace),
            endPlace: this.addressToMovitAddress(booking.toPlace),
            requirements: booking.requirements,
        }

        // Super ugly, but Movit requires a nullable int for transportation permission Id, so convert to the request
        if (booking.transportationPermission && booking.transportationPermission.externalId) {
            const externalId = parseInt(booking.transportationPermission.externalId, 10);
            bookingRequest.transportationPermissionExternalId = isNaN(externalId) ? undefined : externalId;
        }

        if (isDropoffTime) {
            bookingRequest.endTime = bookingRequest.startTime;
            bookingRequest.startTime = undefined;
        }

        return bookingRequest;
    }

    public static toApiBookingUpdateRequestViewModel(booking: Booking): any {

        const orderer: IPassengerViewModel = {
            company: "",
            email: booking.createdByEmail,
            name: booking.createdByNameFromEmail,
            phone: ""
        }

        const passengers: IPassengerViewModel[] = [orderer];
        for (let i = 0; i < booking.unnamedPassengersCount - 1; i++){
            passengers.push({
                company: orderer.company,
                email: "",
                name: "Nimeämätön",
                phone: ""
            })
        }

        const vm: IBookingUpdateRequestViewModel = {
            bookingRefId: booking.id,
            state: booking.stateNameLowerCase,
            pickupDateTime: booking.startDate,
            flights: booking.flights,
            addresses: [booking.fromPlace, booking.toPlace],
            // pickupOption: booking.pickupOption,
            // createProperty: booking.createProperty,
            passengers,
            notes: booking.noteString,
            requirements: booking.requirements,
        }
        return vm;
    }

    static nonLockingStates = [
        BookingStates.Undefined,
        BookingStates.Open
    ]

    static lockingStates = [
        BookingStates.Ok,
        BookingStates.Cancelled,
        BookingStates.Rejected,
        BookingStates.Finished,
    ]

    static lockDaysBeforeStart: number = -1;

    static CopyFrom(booking: Booking): Booking{
        const copy = Object.assign(new Booking(), booking);
        return copy;
    }

    private static addressToMovitAddress(address: IAddressViewModel): IMovitPlace {
        if (!address) {
            return null;
        }
        
        const movitPlace: IMovitPlace = {
            alias: address.address,
            latitude: address.location.lat,
            longitude: address.location.lng,
            streetAddress: address.address,
            city: address.city,
            postalcode: address.postalcode,
            placeType: 0,
            id: 0,
        }
        return movitPlace;
    }
    
}

export default Booking;
